import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import { getSubscriptionStatus, getScheduledCards, getDrafts, deleteCard, getCardHistory, submitFeedback, getUserProfile, updateUserProfile } from "../utils/api";
import Button from "./ui/Button";
import { Edit2, Calendar, Send, Trash2, Eye, Clock, MessageSquare, User, X } from "lucide-react";

export default function UserProfilePage() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [scheduledCards, setScheduledCards] = useState([]);
  const [cardHistory, setCardHistory] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // General feedback state
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showCompleteProfileBanner, setShowCompleteProfileBanner] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [profileUpdating, setProfileUpdating] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [subRes, scheduledRes, draftsRes, historyRes, profileRes] = await Promise.all([
          getSubscriptionStatus(),
          getScheduledCards(),
          getDrafts(),
          getCardHistory(),
          getUserProfile()
        ]);
        setSubscription(subRes.data);
        setScheduledCards(scheduledRes.data);
        setDrafts(draftsRes.data);
        setCardHistory(historyRes.data);
        setProfile(profileRes.data);
        
        // Show complete profile banner if user doesn't have first and last name
        if (!profileRes.data?.first_name || !profileRes.data?.last_name) {
          setShowCompleteProfileBanner(true);
        }
      } catch (err) {
        console.error("Error loading profile data:", err);
        setError("Failed to load some data. Please try refreshing.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Handle general feedback submission
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackText.trim()) return;
    
    setFeedbackSubmitting(true);
    try {
      await submitFeedback(feedbackText.trim(), "general");
      setFeedbackText("");
      setFeedbackSuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => setFeedbackSuccess(false), 3000);
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setError("Failed to submit feedback. Please try again.");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Handle opening the edit profile modal
  const handleOpenEditProfile = () => {
    setEditFirstName(profile?.first_name || "");
    setEditLastName(profile?.last_name || "");
    setShowEditProfileModal(true);
    setShowCompleteProfileBanner(false);
  };

  // Handle profile update
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileUpdating(true);
    try {
      const res = await updateUserProfile({ 
        first_name: editFirstName.trim(), 
        last_name: editLastName.trim() 
      });
      setProfile(prev => ({ 
        ...prev, 
        first_name: res.data.first_name, 
        last_name: res.data.last_name,
        profile_complete: !!(res.data.first_name && res.data.last_name)
      }));
      setShowEditProfileModal(false);
      setShowCompleteProfileBanner(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile. Please try again.");
    } finally {
      setProfileUpdating(false);
    }
  };

  // Helper to get display name
  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    } else if (profile?.first_name) {
      return profile.first_name;
    } else if (profile?.last_name) {
      return profile.last_name;
    }
    return null;
  };

  const handleEditDraft = (draft) => {
    // Check if this is a video card
    const isVideo = draft.media_type === "video" || !!draft.video_url;
    
    navigate(`/edit/${draft.category || "Greeting"}/${draft.id}`, {
      state: {
        src: isVideo ? draft.video_url : draft.image_url,
        frontText: draft.front_text,
        insideText: draft.inside_text,
        layout: draft.layout,
        inside_layout: draft.inside_layout,  // Use snake_case to match EditingPage expectations
        inside_style: draft.inside_style,    // Use snake_case to match EditingPage expectations
        signatures: draft.signatures,
        cardSize: draft.card_size,           // Also pass card dimensions
        isVideo: isVideo,                    // Flag for video cards
        mediaType: draft.media_type || (isVideo ? "video" : "image"),
      },
    });
  };

  const handleEditScheduled = (card) => {
    // Allow editing scheduled cards until they're sent
    // Check if this is a video card
    const isVideo = card.media_type === "video" || !!card.video_url;
    
    navigate(`/edit/${card.category || "Greeting"}/${card.id}`, {
      state: {
        src: isVideo ? card.video_url : card.image_url,
        frontText: card.front_text,
        insideText: card.inside_text,
        layout: card.layout,
        inside_layout: card.inside_layout,  // Use snake_case to match EditingPage expectations
        inside_style: card.inside_style,    // Use snake_case to match EditingPage expectations
        signatures: card.signatures,
        cardSize: card.card_size,           // Also pass card dimensions
        isVideo: isVideo,                   // Flag for video cards
        mediaType: card.media_type || (isVideo ? "video" : "image"),
      },
    });
  };

  const handleDeleteDraft = async (draftId) => {
    if (window.confirm("Are you sure you want to delete this draft? This action cannot be undone.")) {
      setIsDeleting(true);
      try {
        await deleteCard(draftId);
        setDrafts(drafts.filter(draft => draft.id !== draftId));
      } catch (error) {
        console.error("Error deleting draft:", error);
        setError("Failed to delete the draft. Please try again later.");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleViewCard = (card) => {
    // Check if this is a video card
    const isVideo = card.media_type === "video" || !!card.video_url;
    
    // When viewing from profile, always mark as preview
    navigate(`/view/${card.id}`, { 
      state: { 
        preview: true,  // Add preview flag
        src: isVideo ? card.video_url : card.image_url,
        frontText: card.front_text,
        insideText: card.inside_text,
        layout: card.layout,
        insideLayout: card.inside_layout,
        insideStyle: card.inside_style,
        signatures: card.signatures,
        isVideo: isVideo,
        mediaType: card.media_type || (isVideo ? "video" : "image"),
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse space-y-4">
              <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto"></div>
              <div className="h-8 bg-slate-200 rounded-lg w-40 mx-auto"></div>
              <div className="h-4 bg-slate-200 rounded w-48 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <p className="text-slate-700 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Complete Profile Banner */}
        {showCompleteProfileBanner && (
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <User size={24} className="text-white" />
                </div>
                <div className="text-white">
                  <h3 className="font-semibold text-lg">Complete Your Profile</h3>
                  <p className="text-white/80 text-sm">Add your name so recipients know who sent their card!</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleOpenEditProfile}
                  className="bg-white text-indigo-600 hover:bg-indigo-50 px-5 py-2.5 rounded-lg font-medium transition-colors shadow-sm"
                >
                  Add Name
                </Button>
                <button
                  onClick={() => setShowCompleteProfileBanner(false)}
                  className="p-2 text-white/70 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Profile Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white text-2xl font-bold mb-4 shadow-lg shadow-indigo-500/20">
            {profile?.first_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            {getDisplayName() ? `Hi, ${profile.first_name}!` : 'My Profile'}
          </h1>
          {getDisplayName() && (
            <p className="mt-1 text-slate-600 font-medium">{getDisplayName()}</p>
          )}
          <p className="mt-1 text-slate-500">{user?.email}</p>
          <Button
            onClick={handleOpenEditProfile}
            className="mt-4 inline-flex items-center gap-2 bg-slate-100 text-slate-700 hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-colors text-sm"
          >
            <User size={16} />
            {getDisplayName() ? 'Edit Profile' : 'Add Your Name'}
          </Button>
        </div>

        {/* Edit Profile Modal */}
        {showEditProfileModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Edit Profile</h2>
                <button
                  onClick={() => setShowEditProfileModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-100"
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleUpdateProfile} className="p-6">
                <div className="space-y-4 mb-6">
                  <div>
                    <label htmlFor="editFirstName" className="block text-sm font-medium text-slate-700 mb-2">
                      First Name
                    </label>
                    <input
                      id="editFirstName"
                      type="text"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      placeholder="Enter your first name"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-slate-700 placeholder-slate-400 transition-colors"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label htmlFor="editLastName" className="block text-sm font-medium text-slate-700 mb-2">
                      Last Name
                    </label>
                    <input
                      id="editLastName"
                      type="text"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      placeholder="Enter your last name"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-slate-700 placeholder-slate-400 transition-colors"
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    This name will appear when you send cards to recipients.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setShowEditProfileModal(false)}
                    className="flex-1 bg-slate-100 text-slate-700 hover:bg-slate-200 py-3 rounded-xl font-medium transition-colors"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={profileUpdating}
                    className="flex-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 py-3 rounded-xl font-medium transition-colors"
                  >
                    {profileUpdating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Subscription Status */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Plan & Credits</h2>
          </div>
          <div className="p-6">
            {/* Check if user has a subscription OR is a credit user */}
            {(subscription?.active && subscription?.plan_tier !== 'credits' && !subscription?.is_credit_user) ? (
              <div>
                <div className="bg-emerald-50 border border-emerald-200/60 rounded-xl p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-emerald-800 font-semibold">Active {subscription.plan_name}</p>
                      <p className="text-emerald-700 text-sm mt-0.5">You have access to all {subscription.plan_tier} features!</p>
                      {subscription.next_billing_date && (
                        <p className="text-emerald-600 text-sm mt-1">
                          Next billing: {new Date(subscription.next_billing_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Usage Dashboard */}
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4">Usage Dashboard</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Cards per month */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-slate-700">Cards</span>
                        <span className="text-sm text-slate-500">
                          {subscription.limits.cards_per_month === 999999 ? 'Unlimited' : 
                           `${subscription.usage?.cards_per_month?.count || 0} / ${subscription.limits.cards_per_month}`}
                        </span>
                      </div>
                      {subscription.limits.cards_per_month !== 999999 && (
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-indigo-500 h-2 rounded-full transition-all" 
                            style={{ 
                              width: `${Math.min(100, ((subscription.usage?.cards_per_month?.count || 0) / subscription.limits.cards_per_month) * 100)}%` 
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* AI Photos per month */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-slate-700">AI Photos</span>
                        <span className="text-sm text-slate-500">
                          {subscription.limits.ai_photos_per_month === 999999 ? 'Unlimited' : 
                           `${subscription.usage?.ai_photos_per_month?.count || 0} / ${subscription.limits.ai_photos_per_month}`}
                        </span>
                      </div>
                      {subscription.limits.ai_photos_per_month !== 999999 && (
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-indigo-500 h-2 rounded-full transition-all" 
                            style={{ 
                              width: `${Math.min(100, ((subscription.usage?.ai_photos_per_month?.count || 0) / subscription.limits.ai_photos_per_month) * 100)}%` 
                            }}
                          />
                        </div>
                      )}
                      <div className="mt-2 text-xs text-slate-400">
                        Resets: {subscription.usage?.ai_photos_per_month?.last_reset ? 
                          new Date(subscription.usage.ai_photos_per_month.last_reset).toLocaleDateString() : '—'}
                      </div>
                    </div>

                    {/* Templates */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-slate-700">Templates</span>
                        <span className="text-sm font-medium text-indigo-600">
                          {subscription.limits.template_limit === 999999 ? 'Unlimited' : 
                           subscription.limits.template_limit}
                        </span>
                      </div>
                    </div>

                    {/* Collaborators per card */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-slate-700">Collaborators per card</span>
                        <span className="text-sm font-medium text-indigo-600">
                          {subscription.limits.collaborators_per_card === 999999 ? 'Unlimited' : 
                           subscription.limits.collaborators_per_card}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Additional Features */}
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">Features</h4>
                    <div className="flex flex-wrap gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                        !subscription.limits.watermark ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {!subscription.limits.watermark ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        Watermark-free
                      </span>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                        subscription.limits.can_schedule ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {subscription.limits.can_schedule ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        Card scheduling
                      </span>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                        subscription.limits.custom_branding ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {subscription.limits.custom_branding ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        Custom branding
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (subscription?.card_credits > 0 || subscription?.plan_tier === 'credits' || subscription?.is_credit_user) ? (
              /* User has card credits - Pay Per Card user */
              <div>
                <div className="bg-emerald-50 border border-emerald-200/60 rounded-xl p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-emerald-800 font-semibold">Card Credits Active</p>
                      <p className="text-emerald-700 text-sm mt-0.5">You have credits to send beautiful cards anytime!</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-emerald-600">{subscription.card_credits}</div>
                      <div className="text-xs text-emerald-600">credit{subscription.card_credits !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                </div>

                {/* Credit Features */}
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4">Your Benefits</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { text: 'All templates', available: true },
                      { text: 'No watermark', available: true },
                      { text: 'Invite collaborators', available: true },
                      { text: 'Schedule cards', available: true },
                      { text: 'AI-generated photos', available: true },
                      { text: 'Credits never expire', available: true },
                    ].map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-slate-700">{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Buy More Credits CTA */}
                <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                  <Button
                    onClick={() => navigate("/manage")}
                    className="inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm shadow-indigo-600/20"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Buy More Credits
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                  </svg>
                </div>
                <h3 className="text-slate-900 font-semibold mb-1">Get Started Creating</h3>
                <p className="text-slate-500 text-sm mb-5">Choose a subscription for unlimited access or grab a credit pack to create beautiful cards!</p>
                <Button
                  onClick={() => navigate("/subscribe")}
                  className="inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm shadow-indigo-600/20"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  View Options
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Drafts Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">My Drafts</h2>
            {drafts.length > 0 && (
              <span className="text-sm text-slate-500">{drafts.length} draft{drafts.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="p-6">
            {drafts.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Edit2 size={20} className="text-slate-400" />
                </div>
                <p className="text-slate-500">No drafts yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {drafts.map((draft) => {
                  // Check if this is a video card
                  const isVideoDraft = draft.media_type === "video" || !!draft.video_url;
                  const mediaUrl = isVideoDraft ? draft.video_url : draft.image_url;
                  
                  return (
                  <div 
                    key={draft.id} 
                    className="group border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 hover:shadow-lg transition-all duration-200"
                  >
                    {mediaUrl && (
                      <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                        {isVideoDraft ? (
                          <video 
                            src={mediaUrl} 
                            className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                            autoPlay
                            loop
                            muted
                            playsInline
                          />
                        ) : (
                        <img 
                          src={mediaUrl} 
                          alt={draft.title || "Card preview"} 
                          className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = '/placeholder-card.png';
                            e.target.onerror = null;
                          }}
                        />
                        )}
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-medium text-slate-900 truncate">
                        {draft.title || draft.front_text || "Untitled Card"}
                      </h3>
                      <p className="text-xs text-slate-400 mt-1">
                        Edited {new Date(draft.updated_at || draft.created_at).toLocaleDateString()}
                      </p>
                      <div className="mt-4 flex items-center gap-2">
                        <Button
                          onClick={() => handleEditDraft(draft)}
                          className="flex-1 flex items-center justify-center gap-1.5 text-sm bg-indigo-50 text-indigo-600 hover:bg-indigo-100 py-2 rounded-lg font-medium transition-colors"
                        >
                          <Edit2 size={14} />
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleViewCard(draft)}
                          className="px-3 py-2 text-sm bg-slate-50 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          onClick={() => handleDeleteDraft(draft.id)}
                          className="px-3 py-2 text-sm bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors"
                          disabled={isDeleting}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Scheduled Cards Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Scheduled Cards</h2>
            {scheduledCards.length > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                <Clock size={12} />
                {scheduledCards.length} pending
              </span>
            )}
          </div>
          <div className="p-6">
            {scheduledCards.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Calendar size={20} className="text-slate-400" />
                </div>
                <p className="text-slate-500">No scheduled cards yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {scheduledCards.map((card) => (
                  <div key={card.id} className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 hover:shadow-md transition-all">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-900 truncate">{card.title || card.front_text || "Untitled Card"}</h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                          <div className="flex items-center gap-1.5 text-sm text-slate-500">
                            <Calendar size={14} className="text-slate-400" />
                            <span>{new Date(card.send_date).toLocaleDateString()}</span>
                          </div>
                          {card.recipient_email && (
                            <div className="flex items-center gap-1.5 text-sm text-slate-500">
                              <Send size={14} className="text-slate-400" />
                              <span className="truncate">{card.recipient_email}</span>
                            </div>
                          )}
                          {card.opened && !card.opened_by_owner && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                              <Eye size={12} />
                              Opened
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          onClick={() => handleEditScheduled(card)}
                          className="inline-flex items-center gap-1.5 text-sm bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-2 rounded-lg font-medium transition-colors"
                        >
                          <Edit2 size={14} />
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleViewCard(card)}
                          className="inline-flex items-center gap-1.5 text-sm bg-slate-50 text-slate-600 hover:bg-slate-100 px-3 py-2 rounded-lg transition-colors"
                        >
                          <Eye size={14} />
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Card History Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Card History</h2>
            {cardHistory.length > 0 && (
              <span className="text-sm text-slate-500">{cardHistory.length} card{cardHistory.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="p-6">
            {cardHistory.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Clock size={20} className="text-slate-400" />
                </div>
                <p className="text-slate-500">No card history yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cardHistory.map((card) => (
                  <div key={card.id} className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 hover:shadow-md transition-all">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900 truncate">{card.title || card.front_text || "Untitled Card"}</h3>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            card.display_status === 'Sent' ? 'bg-emerald-50 text-emerald-700' :
                            card.display_status === 'Scheduled' ? 'bg-amber-50 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {card.display_status}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                          {card.send_date && (
                            <div className="flex items-center gap-1.5 text-sm text-slate-500">
                              <Calendar size={14} className="text-slate-400" />
                              <span>{card.display_status === "Scheduled" ? "Scheduled:" : "Sent:"} {new Date(card.send_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          {card.recipient_email && (
                            <div className="flex items-center gap-1.5 text-sm text-slate-500">
                              <Send size={14} className="text-slate-400" />
                              <span className="truncate">{card.recipient_email}</span>
                            </div>
                          )}
                          {card.opened_at && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                              <Eye size={12} />
                              Opened {new Date(card.opened_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {card.status === "scheduled" && !card.opened && (
                          <Button
                            onClick={() => handleEditScheduled(card)}
                            className="inline-flex items-center gap-1.5 text-sm bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-2 rounded-lg font-medium transition-colors"
                          >
                            <Edit2 size={14} />
                            Edit
                          </Button>
                        )}
                        <Button
                          onClick={() => handleViewCard(card)}
                          className="inline-flex items-center gap-1.5 text-sm bg-slate-50 text-slate-600 hover:bg-slate-100 px-3 py-2 rounded-lg transition-colors"
                        >
                          <Eye size={14} />
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Provide Feedback Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                <MessageSquare size={18} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Share Your Feedback</h2>
                <p className="text-slate-500 text-sm">Help us improve your experience</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {feedbackSuccess ? (
              <div className="bg-emerald-50 border border-emerald-200/60 rounded-xl p-5 text-center">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-emerald-800 font-medium">Thank you for your feedback!</p>
                <p className="text-emerald-600 text-sm mt-1">We appreciate you taking the time to help us improve.</p>
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit}>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Tell us what you think... suggestions, feature requests, or anything else!"
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-slate-700 placeholder-slate-400 transition-colors"
                />
                <div className="mt-4 flex justify-end">
                  <Button
                    type="submit"
                    disabled={!feedbackText.trim() || feedbackSubmitting}
                    className="inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed px-5 py-2.5 rounded-lg font-medium transition-colors"
                  >
                    {feedbackSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Send Feedback
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Account Management Section */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6 sm:p-8 text-center">
            <h2 className="text-xl font-semibold text-white mb-2">Manage Plan & Credits</h2>
            <p className="text-slate-400 text-sm mb-6">
              {subscription?.active 
                ? "Manage your subscription settings and billing" 
                : subscription?.card_credits > 0
                  ? "Buy more credits or upgrade to a subscription"
                  : "Choose a subscription or purchase credits"
              }
            </p>
            {(subscription?.active || subscription?.card_credits > 0) ? (
              <Button
                onClick={() => navigate("/manage")}
                className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 px-6 py-3 rounded-lg font-medium transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Manage Plan & Credits
              </Button>
            ) : (
              <Button
                onClick={() => navigate("/subscribe")}
                className="inline-flex items-center gap-2 bg-indigo-500 text-white hover:bg-indigo-600 px-6 py-3 rounded-lg font-medium transition-colors shadow-lg shadow-indigo-500/25"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Get Started
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 